/**
 * The format page's plain words (bug fix 1, spec 9 §1–§4). The learner's notes are written for
 * the log ("the model read a total of 85; the mark tags sum to 90, which is used"); a student
 * sees what was decided and why, or nothing. Pure.
 */

const KINDS: Record<string, string> = {
  multiple_choice: "multiple choice",
  short_answer: "short answer",
  extended: "extended answer",
  calculation: "calculation",
  code_trace: "code trace",
};

export const kindOf = (type: string) => KINDS[type] ?? type.replace(/_/g, " ");

/** A learner note as the student reads it, or null for one that doesn't matter to them. */
export function plainNote(note: string): string | null {
  const total = /mark tags sum to (\d+)/.exec(note);
  if (total) return `Your past papers add up to ${total[1]} marks, so that's the total we use.`;
  const range = /mark tags give \[(\d+),\s*(\d+)\]/.exec(note);
  if (range)
    return `Questions in your past papers are worth ${range[1]} to ${range[2]} marks, so that's the range we use.`;
  const dropped = /dropped question types with no marks or share: (.+)$/.exec(note);
  if (dropped) {
    const kinds = dropped[1]!.split(",").map((t) => kindOf(t.trim()));
    return `Some kinds of question had no marks in your past papers, so we left them out: ${kinds.join(", ")}.`;
  }
  return null;
}

/** "2 hours", "1 hour 30 min", "45 min". */
export function duration(minutes: number): string {
  if (minutes <= 0) return "";
  if (minutes < 60) return `${minutes} min`;
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  const hours = `${h} hour${h === 1 ? "" : "s"}`;
  return m ? `${hours} ${m} min` : hours;
}

const list = (parts: string[]) =>
  parts.length < 2 ? parts.join("") : `${parts.slice(0, -1).join(", ")} and ${parts.at(-1)}`;

/** "In a Standard paper (40 marks): about 8 marks of multiple choice, 18 of short answer…" */
export function splitLine(split: { type: string; marks: number }[], paper = 40): string {
  const parts = split
    .filter((s) => s.marks > 0)
    .map((s, i) =>
      i === 0 ? `${s.marks} marks of ${kindOf(s.type)}` : `${s.marks} of ${kindOf(s.type)}`,
    );
  if (parts.length === 0) return "";
  return `In a Standard paper (${paper} marks): about ${list(parts)}.`;
}
